import { capitalize } from '@avantstay/backoffice-core'

import { DistributionChannelsSubSection } from '@new/domains/properties/screens/propertyDistributionChannels/DistributionChannels.types'

import {
  formatBooleanTextToText,
  formatBooleanToText,
  formatPetPolicy,
} from '../../Distribution.utils'
import {
  DistributionChannelDataField,
  DistributionChannelInfoField,
} from '../OtaDistributionChannel.types'

const listingDescriptionFields: DistributionChannelDataField = {
  title: {
    title: 'Listing Name',
  },
  description: {
    title: 'Summary',
  },
  space: {
    title: 'The Space',
  },
  access: {
    title: 'Guest Access',
  },
  interaction: {
    title: 'Interaction',
  },
  neighborhood: {
    title: 'Neighborhood overview',
  },
  transit: {
    title: 'Transit',
  },
  notes: {
    title: 'Other Things to Note',
  },
  houseRules: {
    title: 'House Rules',
  },
}

const listingInfoDataFields: DistributionChannelDataField = {
  'distributionSettings.airbnbSettings.listingType': {
    title: 'Airbnb Listing Type',
  },
  propertyTypeGroup: {
    title: 'Airbnb Property Type Group',
  },
  propertyType: {
    title: 'Airbnb Property Type',
  },
  'distributionSettings.airbnbSettings.checkInOptionCategory': {
    title: 'Airbnb Check-in Option',
  },
  'distributionSettings.airbnbSettings.synchronizationCategory': {
    title: 'Airbnb Synchronization Category',
    format: value => capitalize(value.toString().replace(/_/g, ' ')),
  },
  'distributionSettings.airbnbSettings.permitId': {
    title: 'Permit ID',
  },
}

const propertyDetailsDataFields: DistributionChannelDataField = {
  'distributionSettings.airbnbSettings.leadTime': {
    title: 'Airbnb booking lead time',
  },
  'distributionSettings.airbnbSettings.directions': {
    title: 'Directions',
  },
  'distributionSettings.airbnbSettings.manual': {
    title: 'House Manual',
  },
  'distributionSettings.airbnbSettings.roomNumber': {
    title: 'Room Number',
  },
  'distributionSettings.commonChannelSettings.hasBedInCommonSpace': {
    title: 'Bed in Common Space',
    format: value => formatBooleanToText(value),
  },
  'distributionSettings.commonChannelSettings.hasInternet': {
    title: 'Internet',
    format: value => formatBooleanToText(value),
  },
  'distributionSettings.commonChannelSettings.hasWheelchairAccess': {
    title: 'WheelChair Accessible',
    format: value => formatBooleanToText(value),
  },
  'homeInfo.petPolicy': {
    title: 'Pet Policy',
    format: value => formatPetPolicy(value),
  },
  'homeInfo.petCount': {
    title: 'Pet Count',
  },
}

const guestControls: DistributionChannelDataField = {
  'homeInfo.eventsAllowed': {
    title: 'Events Allowed?',
    format: value => formatBooleanTextToText(value),
  },
  'homeInfo.childrenAllowed': {
    title: 'Children Allowed?',
    format: value => formatBooleanTextToText(value),
  },
  'homeInfo.smokingAllowed': {
    title: 'Smoking Allowed?',
    format: value => formatBooleanTextToText(value),
  },
}

export const airbnbListingDataGroups: DistributionChannelInfoField[] = [
  {
    title: 'Listing Description',
    dataFields: listingDescriptionFields,
    sectionKey: DistributionChannelsSubSection.listingDescription,
  },
  {
    title: 'Listing Info',
    dataFields: listingInfoDataFields,
    sectionKey: DistributionChannelsSubSection.listingInfo,
  },
  {
    title: 'Listing Locked Attributes',
    description: [
      'Airbnb locked attributes are attributes that have been manually edited on the Airbnb UI.',
      'They are locked from being edited using the integration API, so any updates made in the backoffice are ignored until they are unlocked again on Airbnb.',
      'To unlock them, find the listing on the Airbnb UI, open its preferences, and choose to manage it from the connected app.',
      'This unlocks all locked attributes at once.',
    ],
    descriptionLink: {
      label: 'Learn more on Airbnb',
      url: 'https://www.airbnb.com/help/article/2973',
    },
    sectionKey: DistributionChannelsSubSection.listingLockedAttributes,
  },
  {
    title: 'Property Details',
    dataFields: propertyDetailsDataFields,
    sectionKey: DistributionChannelsSubSection.propertyDetails,
  },
  {
    title: 'Guest Controls',
    dataFields: guestControls,
    sectionKey: DistributionChannelsSubSection.guestControls,
  },
]
